import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { marked } from 'marked';
import type { ChatMessage } from '@chatbot-shop/shared';
import { parseSSE } from '@chatbot-shop/shared';

marked.setOptions({ gfm: true, breaks: true });
marked.use({
  renderer: {
    link(href: string, _title: string | null | undefined, text: string): string {
      return `<a href="${href}" target="_blank" rel="noopener nofollow">${text}</a>`;
    },
  },
});

type Phase = 'idle' | 'searching' | 'thinking' | 'streaming';
type UIMessage = ChatMessage & {
  id: string;
  pending?: boolean;
  phase?: Phase;
  thinkingStartMs?: number;
  thinkingTokens?: number;
};

@customElement('chatbot-shop')
export class ChatbotShop extends LitElement {
  @property({ type: String }) endpoint = '';
  @property({ type: String }) shop = '';
  @property({ type: String }) heading = 'Assistente';
  @property({ type: String, attribute: 'pinned-text' }) pinnedText = '';
  @property({ type: String, attribute: 'pinned-url' }) pinnedUrl = '';
  @property({ type: String, attribute: 'pinned-cta' }) pinnedCta = '';

  @state() private open = false;
  @state() private messages: UIMessage[] = [];
  @state() private input = '';
  @state() private sending = false;
  @state() private thinkingTick = 0;
  private thinkingTimer: number | null = null;

  // Persistenza conversazione (localStorage, TTL 24h, scoped per shop)
  private static readonly THREAD_KEY = 'chatbot-shop:thread:v1';
  private static readonly THREAD_TTL_MS = 24 * 60 * 60 * 1000;
  private threadStorageKey(): string {
    return `${ChatbotShop.THREAD_KEY}:${this.shop}`;
  }
  private loadThread(): void {
    if (!this.shop) return;
    try {
      const raw = localStorage.getItem(this.threadStorageKey());
      if (!raw) return;
      const parsed = JSON.parse(raw) as { messages?: UIMessage[]; updatedAt?: number };
      if (!parsed.messages || !parsed.updatedAt) return;
      if (Date.now() - parsed.updatedAt > ChatbotShop.THREAD_TTL_MS) {
        localStorage.removeItem(this.threadStorageKey());
        return;
      }
      this.messages = parsed.messages
        .filter((m) => m.content && !m.pending)
        .map((m) => ({ id: m.id, role: m.role, content: m.content }));
    } catch {
      // skip silenzioso (incognito / quota piena)
    }
  }
  private saveThread(): void {
    if (!this.shop) return;
    const completed = this.messages
      .filter((m) => m.content && !m.pending)
      .map((m) => ({ id: m.id, role: m.role, content: m.content }));
    try {
      if (completed.length === 0) {
        localStorage.removeItem(this.threadStorageKey());
        return;
      }
      localStorage.setItem(
        this.threadStorageKey(),
        JSON.stringify({ messages: completed, updatedAt: Date.now() }),
      );
    } catch {
      // skip silenzioso
    }
  }
  private clearThread = () => {
    try { localStorage.removeItem(this.threadStorageKey()); } catch { /* */ }
    this.messages = [];
  };

  // ETA baseline: medie auto-apprese dalle ultime N risposte completate.
  // Defaults conservativi finché non ci sono campioni.
  private static readonly STORAGE_KEY = 'chatbot-shop:thinking-stats:v1';
  private static readonly MAX_SAMPLES = 20;
  private static readonly DEFAULT_AVG_MS = 8000;
  private static readonly DEFAULT_AVG_TOKENS = 120;
  private get baselineMs(): number {
    return this.loadStats().avgMs ?? ChatbotShop.DEFAULT_AVG_MS;
  }
  private get baselineTokens(): number {
    return this.loadStats().avgTokens ?? ChatbotShop.DEFAULT_AVG_TOKENS;
  }
  private loadStats(): { avgMs?: number; avgTokens?: number } {
    try {
      const raw = localStorage.getItem(ChatbotShop.STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as { samples?: Array<{ ms: number; tokens: number }> };
      const samples = parsed.samples ?? [];
      if (samples.length === 0) return {};
      const out: { avgMs?: number; avgTokens?: number } = {};
      out.avgMs = samples.reduce((a, s) => a + s.ms, 0) / samples.length;
      const withTokens = samples.filter((s) => s.tokens > 0);
      if (withTokens.length > 0) {
        out.avgTokens = withTokens.reduce((a, s) => a + s.tokens, 0) / withTokens.length;
      }
      return out;
    } catch {
      return {};
    }
  }
  private recordSample(ms: number, tokens: number): void {
    if (ms <= 0) return;
    try {
      const raw = localStorage.getItem(ChatbotShop.STORAGE_KEY);
      const parsed = raw ? (JSON.parse(raw) as { samples?: Array<{ ms: number; tokens: number }> }) : {};
      const samples = parsed.samples ?? [];
      samples.push({ ms, tokens });
      while (samples.length > ChatbotShop.MAX_SAMPLES) samples.shift();
      localStorage.setItem(ChatbotShop.STORAGE_KEY, JSON.stringify({ samples }));
    } catch {
      // localStorage non disponibile o pieno: skip silenzioso
    }
  }

  static override styles = css`
    :host {
      --primary: #111;
      --bg: #fff;
      --user-bg: #111;
      --user-fg: #fff;
      --assistant-bg: #f1f1f3;
      --assistant-fg: #111;
      --border: #e5e5e7;
      font-family: system-ui, -apple-system, sans-serif;
      position: fixed;
      right: 20px;
      bottom: 20px;
      z-index: 2147483000;
    }

    button.fab {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      border: none;
      background: var(--primary);
      color: white;
      cursor: pointer;
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
      font-size: 1.5rem;
    }

    .backdrop { display: none; }
    @media (max-width: 600px) {
      .backdrop {
        display: block;
        position: fixed;
        inset: 0;
        z-index: 1;
        background: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(2px);
        -webkit-backdrop-filter: blur(2px);
        animation: backdrop-in 0.18s ease-out;
      }
      @keyframes backdrop-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }
    }

    .panel {
      position: relative;
      z-index: 2;
      width: min(380px, calc(100vw - 40px));
      height: min(560px, calc(100vh - 40px));
      background: var(--bg);
      border-radius: 12px;
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      border: 1px solid var(--border);
    }

    header {
      padding: 0.75rem 1rem;
      background: var(--primary);
      color: white;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-weight: 600;
    }
    .header-actions {
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }
    header .icon-btn {
      background: transparent;
      border: none;
      color: white;
      font-size: 1.1rem;
      cursor: pointer;
      line-height: 1;
      padding: 0.25rem 0.4rem;
      border-radius: 6px;
    }
    header .icon-btn:hover { background: rgba(255, 255, 255, 0.12); }

    .pinned {
      padding: 0.5rem 0.75rem;
      background: #25D366;
      color: white;
      font-size: 0.8rem;
      line-height: 1.3;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    }
    .pinned-text { flex: 1; }
    .pinned a.pinned-cta {
      color: white;
      text-decoration: underline;
      font-weight: 600;
      white-space: nowrap;
      padding: 0.15rem 0.4rem;
      border: 1px solid rgba(255, 255, 255, 0.4);
      border-radius: 999px;
      text-decoration: none;
    }
    .pinned a.pinned-cta:hover { background: rgba(255, 255, 255, 0.15); }

    .messages {
      flex: 1;
      overflow-y: auto;
      padding: 1rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .msg {
      max-width: 85%;
      padding: 0.5rem 0.75rem;
      border-radius: 12px;
      line-height: 1.4;
      font-size: 0.9rem;
      word-wrap: break-word;
    }
    .msg.user {
      align-self: flex-end;
      background: var(--user-bg);
      color: var(--user-fg);
      border-bottom-right-radius: 4px;
    }
    .msg.assistant {
      align-self: flex-start;
      background: var(--assistant-bg);
      color: var(--assistant-fg);
      border-bottom-left-radius: 4px;
    }
    .msg p { margin: 0 0 0.5rem; }
    .msg p:last-child { margin-bottom: 0; }
    .msg.assistant a {
      color: var(--assistant-fg);
      text-decoration: underline;
      font-weight: 600;
    }
    .msg.assistant a:hover {
      opacity: 0.7;
    }
    .msg.assistant ul, .msg.assistant ol {
      margin: 0.25rem 0;
      padding-left: 1.25rem;
    }
    .msg.assistant li { margin: 0.15rem 0; }
    .msg.thinking {
      font-style: italic;
      opacity: 0.7;
      white-space: nowrap;
    }
    .msg.thinking .elapsed {
      font-style: normal;
      font-variant-numeric: tabular-nums;
      opacity: 0.7;
      margin-left: 0.4em;
      font-size: 0.85em;
    }
    .msg.thinking {
      min-width: 12rem;
      white-space: normal;
    }
    .thinking-head {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: 0.5rem;
      white-space: nowrap;
    }
    .eta-track {
      margin-top: 0.4rem;
      height: 3px;
      background: rgba(0, 0, 0, 0.08);
      border-radius: 999px;
      overflow: hidden;
    }
    .eta-fill {
      height: 100%;
      background: var(--primary);
      border-radius: 999px;
      transition: width 0.4s ease-out;
      animation: eta-pulse 1.6s ease-in-out infinite;
    }
    @keyframes eta-pulse {
      0%, 100% { opacity: 0.6; }
      50% { opacity: 1; }
    }
    @media (prefers-reduced-motion: reduce) {
      .eta-fill { animation: none; opacity: 0.85; }
    }
    .msg.thinking .dots {
      display: inline-block;
      margin-left: 0.15em;
    }
    .msg.thinking .dots span {
      display: inline-block;
      opacity: 0;
      animation: dot-fade 1.2s infinite;
    }
    .msg.thinking .dots span:nth-child(2) { animation-delay: 0.2s; }
    .msg.thinking .dots span:nth-child(3) { animation-delay: 0.4s; }
    @keyframes dot-fade {
      0%, 80%, 100% { opacity: 0; }
      40% { opacity: 1; }
    }

    form {
      display: flex;
      gap: 0.5rem;
      padding: 0.75rem;
      border-top: 1px solid var(--border);
    }
    input {
      flex: 1;
      padding: 0.5rem 0.75rem;
      border: 1px solid var(--border);
      border-radius: 999px;
      font-size: 0.9rem;
      outline: none;
    }
    /* iOS Safari fa auto-zoom su input con font-size < 16px: lo blocchiamo su mobile. */
    @media (max-width: 600px) {
      input { font-size: 16px; }
    }
    input:focus { border-color: var(--primary); }
    button.send {
      background: var(--primary);
      color: white;
      border: none;
      border-radius: 999px;
      padding: 0 1rem;
      cursor: pointer;
      font-weight: 600;
    }
    button.send:disabled { opacity: 0.5; cursor: not-allowed; }
  `;

  override connectedCallback(): void {
    super.connectedCallback();
    if (!this.endpoint) {
      console.warn('[chatbot-shop] missing endpoint attribute');
    }
    this.loadThread();
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('messages')) this.saveThread();
  }

  private toggle = () => { this.open = !this.open; };

  private onInput = (e: Event) => {
    this.input = (e.target as HTMLInputElement).value;
  };

  private submit = async (e: Event) => {
    e.preventDefault();
    const text = this.input.trim();
    if (!text || this.sending) return;

    const userMsg: UIMessage = { id: crypto.randomUUID(), role: 'user', content: text };
    const assistantId = crypto.randomUUID();
    const assistantMsg: UIMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      pending: true,
      phase: 'idle',
    };
    this.messages = [...this.messages, userMsg, assistantMsg];
    this.input = '';
    this.sending = true;

    try {
      await this.stream(assistantId);
    } catch (err) {
      this.patchMessage(assistantId, {
        content: '⚠️ Errore di connessione. Riprova.',
        pending: false,
        phase: 'idle',
      });
    } finally {
      this.sending = false;
      this.stopThinkingTimer();
    }
  };

  private startThinkingTimer(): void {
    if (this.thinkingTimer !== null) return;
    this.thinkingTick = 0;
    this.thinkingTimer = window.setInterval(() => {
      this.thinkingTick++;
    }, 1000);
  }

  private stopThinkingTimer(): void {
    if (this.thinkingTimer !== null) {
      clearInterval(this.thinkingTimer);
      this.thinkingTimer = null;
    }
  }

  override disconnectedCallback(): void {
    this.stopThinkingTimer();
    super.disconnectedCallback();
  }

  private async stream(assistantId: string): Promise<void> {
    const history = this.messages
      .filter((m) => !m.pending)
      .map(({ role, content }) => ({ role, content }));

    const res = await fetch(`${this.endpoint}/chat`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ shop: this.shop, messages: history }),
    });
    if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

    const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
    let acc = '';
    let buf = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += value;
      const lastBreak = buf.lastIndexOf('\n\n');
      if (lastBreak === -1) continue;
      const ready = buf.slice(0, lastBreak);
      buf = buf.slice(lastBreak + 2);

      for (const ev of parseSSE(ready)) {
        if (ev.type === 'searching') {
          this.patchMessage(assistantId, { phase: 'searching' });
        } else if (ev.type === 'thinking') {
          const current = this.messages.find((m) => m.id === assistantId);
          this.patchMessage(assistantId, {
            phase: 'thinking',
            thinkingStartMs: current?.thinkingStartMs ?? Date.now(),
            thinkingTokens: ev.tokens ?? current?.thinkingTokens ?? 0,
          });
          this.startThinkingTimer();
        } else if (ev.type === 'token') {
          if (acc === '') {
            // primo token visibile: registra il sample del thinking concluso
            const m = this.messages.find((x) => x.id === assistantId);
            if (m?.thinkingStartMs) {
              this.recordSample(Date.now() - m.thinkingStartMs, m.thinkingTokens ?? 0);
            }
          }
          acc += ev.value;
          this.patchMessage(assistantId, { content: acc, phase: 'streaming' });
          this.stopThinkingTimer();
        } else if (ev.type === 'done') {
          this.patchMessage(assistantId, { pending: false, phase: 'idle' });
          this.stopThinkingTimer();
        } else if (ev.type === 'error') {
          this.stopThinkingTimer();
          throw new Error(ev.message);
        }
      }
    }
    this.patchMessage(assistantId, { pending: false });
  }

  private patchMessage(id: string, patch: Partial<UIMessage>): void {
    this.messages = this.messages.map((m) => (m.id === id ? { ...m, ...patch } : m));
  }

  private renderMessage(m: UIMessage) {
    if (m.role === 'assistant' && m.pending && !m.content) {
      const phase = m.phase ?? 'idle';
      if (phase === 'searching') {
        // Neutro di proposito: l'evento `searching` parte prima del retrieval,
        // quindi qui non si sa ancora se dall'altra parte c'è un catalogo o un
        // sito di contenuto. "Cerco nel catalogo" su un sito di servizi è
        // semplicemente falso.
        return html`<div class="msg assistant thinking">Cerco<span class="dots"><span>.</span><span>.</span><span>.</span></span></div>`;
      }
      if (phase === 'thinking') {
        const elapsedMs = m.thinkingStartMs ? Date.now() - m.thinkingStartMs : 0;
        const elapsed = Math.max(0, Math.floor(elapsedMs / 1000));
        const tokens = m.thinkingTokens ?? 0;
        const ratio = Math.max(elapsedMs / this.baselineMs, tokens / this.baselineTokens);
        const overrun = ratio >= 1;
        const pct = overrun ? 95 : Math.round(ratio * 95);
        const headLabel = overrun ? 'Ancora un attimo' : 'Sto ragionando';
        const meta = elapsed >= 1
          ? html` <span class="elapsed">${tokens > 0 ? `${elapsed}s · ${tokens} token` : `${elapsed}s`}</span>`
          : nothing;
        return html`
          <div class="msg assistant thinking">
            <div class="thinking-head">
              <span>${headLabel}<span class="dots"><span>.</span><span>.</span><span>.</span></span></span>
              ${meta}
            </div>
            <div class="eta-track"><div class="eta-fill" style="width: ${pct}%"></div></div>
          </div>
        `;
      }
      // idle / streaming non ancora popolato: placeholder discreto
      return html`<div class="msg assistant thinking">…</div>`;
    }
    const rendered = m.role === 'assistant'
      ? marked.parse(m.content || '', { async: false }) as string
      : m.content;
    return html`
      <div class="msg ${m.role}" .innerHTML=${rendered}></div>
    `;
  }

  override render() {
    if (!this.open) {
      return html`<button class="fab" @click=${this.toggle} aria-label="Apri chat">💬</button>`;
    }
    return html`
      <div class="backdrop" @click=${this.toggle} aria-hidden="true"></div>
      <div class="panel" role="dialog" aria-label=${this.heading}>
        <header>
          <span>${this.heading}</span>
          <div class="header-actions">
            ${this.messages.length > 0
              ? html`<button
                  class="icon-btn"
                  @click=${this.clearThread}
                  title="Nuova conversazione"
                  aria-label="Nuova conversazione">↻</button>`
              : nothing}
            <button class="icon-btn" @click=${this.toggle} aria-label="Chiudi">✕</button>
          </div>
        </header>
        ${this.pinnedText
          ? html`<div class="pinned">
              <span class="pinned-text">${this.pinnedText}</span>
              ${this.pinnedUrl
                ? html`<a class="pinned-cta" href=${this.pinnedUrl} target="_blank" rel="noopener noreferrer">${this.pinnedCta || 'Apri'}</a>`
                : nothing}
            </div>`
          : nothing}
        <div class="messages">
          ${this.messages.length === 0
            ? html`<div class="msg assistant">Ciao! Come posso aiutarti?</div>`
            : nothing}
          ${this.messages.map((m) => this.renderMessage(m))}
        </div>
        <form @submit=${this.submit}>
          <input
            type="text"
            .value=${this.input}
            @input=${this.onInput}
            placeholder="Scrivi un messaggio…"
            ?disabled=${this.sending}
            autocomplete="off"
          />
          <button class="send" type="submit" ?disabled=${this.sending || !this.input.trim()}>
            Invia
          </button>
        </form>
      </div>
    `;
  }
}

// currentScript è disponibile solo durante l'esecuzione sincrona dello script;
// dentro un listener async (DOMContentLoaded) è null. Lo catturiamo subito.
const SCRIPT_EL = typeof document !== 'undefined'
  ? (document.currentScript as HTMLScriptElement | null)
  : null;

function autoMount(): void {
  if (!SCRIPT_EL) return;
  const endpoint = SCRIPT_EL.dataset.endpoint;
  const shop = SCRIPT_EL.dataset.shop;
  if (!endpoint || !shop) return;
  if (document.querySelector('chatbot-shop')) return;
  const el = document.createElement('chatbot-shop');
  el.setAttribute('endpoint', endpoint);
  el.setAttribute('shop', shop);
  for (const key of ['pinned-text', 'pinned-url', 'pinned-cta'] as const) {
    const val = SCRIPT_EL.dataset[toCamel(key)];
    if (val) el.setAttribute(key, val);
  }
  document.body.appendChild(el);
}

function toCamel(kebab: string): string {
  return kebab.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoMount);
  } else {
    autoMount();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'chatbot-shop': ChatbotShop;
  }
}
